import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
  Card,
  Collapse,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { authFetch } from "../utils/authFetch";

export default function EFacturaList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [err, setErr] = useState("");

  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(20);

  const [docNo, setDocNo] = useState("");
  const [client, setClient] = useState("");
  const [stareFilter, setStareFilter] = useState("");

  const [selectedIds, setSelectedIds] = useState(new Set());

  // Token Config State
  const [showTokenConfig, setShowTokenConfig] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [refreshTokenInput, setRefreshTokenInput] = useState("");
  const [tokenExpiryDays, setTokenExpiryDays] = useState(90);
  const [savingToken, setSavingToken] = useState(false);

  // OAuth States & Handlers
  const [oauthLoading, setOauthLoading] = useState(false);
  const [refreshingToken, setRefreshingToken] = useState(false);
  const [tokenStatus, setTokenStatus] = useState(null);

  useEffect(() => {
    if (tokenStatus) {
      setTokenInput(tokenStatus.token || "");
      setRefreshTokenInput(tokenStatus.refresh_token || "");
    }
  }, [tokenStatus]);

  const handleOAuthRedirect = async () => {
    try {
      const res = await authFetch("/efactura/oauth-url");
      if (res.configured && res.url) {
        window.location.href = res.url;
      } else {
        toast.error(res.message || "Eroare la obținerea URL-ului de autorizare.");
      }
    } catch (e) {
      toast.error(`Nu s-a putut genera link-ul OAuth: ${e?.message || e}`);
    }
  };

  const handleManualRefresh = async () => {
    setRefreshingToken(true);
    try {
      const res = await authFetch("/efactura/refresh-token-manual", {
        method: "POST",
      });
      toast.success(res.message || "Tokenul a fost reîmprospătat cu succes!");
      load();
    } catch (e) {
      toast.error(`Eroare la reîmprospătarea tokenului: ${e?.message || e}`);
    } finally {
      setRefreshingToken(false);
    }
  };

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    p.set("skip", String(skip));
    p.set("limit", String(limit));
    if (docNo.trim()) p.set("doc_no", docNo.trim());
    if (client.trim()) p.set("client", client.trim());
    if (stareFilter) p.set("stare", stareFilter);
    return p.toString();
  }, [skip, limit, docNo, client, stareFilter]);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await authFetch(`/efactura/?${qs}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
      
      // Fetch token status
      const status = await authFetch("/efactura/token-status");
      setTokenStatus(status);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    
    if (code) {
      const exchangeCode = async () => {
        setOauthLoading(true);
        toast.info("Se procesează codul de autorizare ANAF...");
        try {
          const res = await authFetch("/efactura/oauth-callback", {
            method: "POST",
            body: JSON.stringify({ code }),
          });
          toast.success(res.message || "Tokenul ANAF a fost obținut cu succes!");
          // Curatam parametrul code din URL fara refresh
          const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
          window.history.replaceState({ path: newUrl }, "", newUrl);
        } catch (e) {
          setErr(String(e?.message || e));
          toast.error("Eroare la schimbul de cod OAuth cu ANAF.");
        } finally {
          setOauthLoading(false);
          load();
        }
      };
      exchangeCode();
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qs]);

  const canPrev = skip > 0;
  const canNext = skip + limit < total;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = items.map((it) => it.id);
      setSelectedIds(new Set(allIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id, checked) => {
    const newSel = new Set(selectedIds);
    if (checked) {
      newSel.add(id);
    } else {
      newSel.delete(id);
    }
    setSelectedIds(newSel);
  };

  const handleSendSpv = async () => {
    if (selectedIds.size === 0) return;
    setSending(true);
    setErr("");
    try {
      const idsArr = Array.from(selectedIds);
      const res = await authFetch("/efactura/send-spv", {
        method: "POST",
        body: JSON.stringify({ efactura_ids: idsArr }),
      });
      if (res.errors && res.errors.length > 0) {
        toast.warning(`Trimise cu succes: ${res.success_count}. Erori semnalate.`);
        setErr(res.errors.join("\n"));
      } else {
        toast.success(res.message || "Facturile selectate au fost trimise în SPV.");
      }
      load();
    } catch (e) {
      setErr(String(e?.message || e));
      toast.error("Eroare trimitere SPV.");
    } finally {
      setSending(false);
    }
  };

  const handleCheckStare = async (id) => {
    setCheckingId(id);
    try {
      const res = await authFetch(`/efactura/stare-spv/${id}`, {
        method: "POST",
      });
      toast.info(`Stare factură: ${res.raspuns_anaf || res.status_spv}`);
      load();
    } catch (e) {
      toast.error(`Eroare verificare stare: ${e?.message || e}`);
    } finally {
      setCheckingId(null);
    }
  };

  const handleDownloadPdf = async (id, docNoStr) => {
    setDownloadingId(id);
    try {
      const blob = await authFetch(`/efactura/pdf/${id}`, {
        responseType: "blob",
      });
      const file = new Blob([blob], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, "_blank");
      toast.success("PDF generat cu succes.");
    } catch (e) {
      toast.error(`Eroare generare PDF: ${e?.message || e}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSaveToken = async (e) => {
    e.preventDefault();
    if (!tokenInput.trim() || !refreshTokenInput.trim()) {
      toast.warning("Introduceți atât Tokenul cât și Refresh Tokenul.");
      return;
    }
    setSavingToken(true);
    try {
      await authFetch("/efactura/token", {
        method: "POST",
        body: JSON.stringify({
          token: tokenInput.trim(),
          refresh_token: refreshTokenInput.trim(),
          expiry_days: Number(tokenExpiryDays),
        }),
      });
      toast.success("Tokenul ANAF SPV a fost salvat cu succes.");
      setShowTokenConfig(false);
      load();
    } catch (err) {
      toast.error(`Eroare la salvarea tokenului: ${err?.message || err}`);
    } finally {
      setSavingToken(false);
    }
  };

  const allSelected = items.length > 0 && selectedIds.size === items.length;

  const renderBadge = (status, answer, details) => {
    let bg = "secondary";
    let text = status;

    if (status === "ok" || status === "ANAF OK" || answer === "ANAF OK") {
      bg = "success";
      text = "ANAF OK";
    } else if (status === "TRIMISA" || status === "Incarcata") {
      bg = "primary";
      text = "Trimisă";
    } else if (status === "EROARE" || status === "ANAF NOK" || status === "Erori") {
      bg = "danger";
      text = "Eroare ANAF";
    } else if (status === "Nesent") {
      bg = "secondary";
      text = "De trimis";
    }

    return (
      <div title={details || ""}>
        <Badge bg={bg} className="px-2 py-1" style={{ fontSize: "0.85rem", fontWeight: "500" }}>
          {text}
        </Badge>
      </div>
    );
  };

  return (
    <Container fluid className="py-4 px-lg-5" style={{ minHeight: "90vh", background: "#f8f9fa" }}>
      <style>{`
        .custom-card {
          border: none;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.05);
          background: #ffffff;
        }
        .custom-table {
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.02);
        }
        .custom-table th {
          background: #343a40;
          color: #ffffff;
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.78rem;
          letter-spacing: 0.5px;
          padding: 12px 10px;
          border: none;
        }
        .custom-table td {
          vertical-align: middle;
          padding: 12px 10px;
          font-size: 0.9rem;
          color: #495057;
          border-bottom: 1px solid #dee2e6;
        }
        .custom-table tr:hover td {
          background-color: #f1f3f5;
        }
        .gradient-header {
          background: linear-gradient(135deg, #212529 0%, #343a40 100%);
          color: white;
          padding: 20px 24px;
          border-radius: 12px;
          margin-bottom: 24px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }
        .btn-action-view {
          padding: 4px 10px;
          font-size: 0.8rem;
          font-weight: 500;
          border-radius: 6px;
        }
      `}</style>

      {/* OAuth Exchange Loader */}
      {oauthLoading && (
        <Alert variant="info" className="custom-card p-4 mb-4 text-center d-flex flex-column align-items-center justify-content-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <h5 className="mb-1 text-dark fw-bold">Se finalizează autorizarea cu ANAF...</h5>
          <p className="mb-0 text-muted small">Vă rugăm să așteptați. Schimbăm codul de autorizare pentru token-uri.</p>
        </Alert>
      )}

      {/* Token status warning alerts */}
      {!oauthLoading && tokenStatus && !tokenStatus.is_mock && (
        <>
          {!tokenStatus.has_token && (
            <Alert variant="danger" className="custom-card p-4 mb-4 border d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <h5 className="alert-heading fw-bold mb-1" style={{ color: "#d90429" }}>⚠️ Token SPV lipsă</h5>
                <p className="mb-1 text-muted small">
                  Nu există un token ANAF configurat pentru această firmă. Aveți nevoie de un token pentru a transmite facturi în SPV.
                </p>
                <small className="text-danger fw-semibold d-block mt-1" style={{ fontSize: "0.8rem" }}>
                  * Notă: Asigurați-vă că aveți stick-ul USB (token-ul fizic) cu semnătura electronică conectat la calculator (sau certificatul soft instalat) înainte de a apăsa butonul.
                </small>
              </div>
              <Button 
                variant="danger" 
                onClick={handleOAuthRedirect} 
                className="btn-action-view px-4 py-2 fw-bold shadow-sm"
              >
                🌐 Obține Token Automat
              </Button>
            </Alert>
          )}

          {tokenStatus.has_token && tokenStatus.is_expired && (
            <Alert variant="danger" className="custom-card p-4 mb-4 border d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <h5 className="alert-heading fw-bold mb-1" style={{ color: "#d90429" }}>⚠️ Token SPV expirat</h5>
                <p className="mb-1 text-muted small">
                  Tokenul existent ANAF SPV a expirat la data de <strong>{tokenStatus.expiry_date ? new Date(tokenStatus.expiry_date).toLocaleDateString("ro-RO") : "-"}</strong>.
                </p>
                <small className="text-danger fw-semibold d-block mt-1" style={{ fontSize: "0.8rem" }}>
                  * Notă: Asigurați-vă că aveți stick-ul USB (token-ul fizic) cu semnătura electronică conectat la calculator (sau certificatul soft instalat) înainte de a apăsa butonul.
                </small>
              </div>
              <Button 
                variant="danger" 
                onClick={handleOAuthRedirect} 
                className="btn-action-view px-4 py-2 fw-bold shadow-sm"
              >
                🌐 Reînnoiește Token Automat
              </Button>
            </Alert>
          )}

          {tokenStatus.has_token && !tokenStatus.is_expired && tokenStatus.days_left !== null && tokenStatus.days_left <= 5 && (
            <Alert variant="warning" className="custom-card p-4 mb-4 border d-flex flex-wrap align-items-center justify-content-between">
              <div>
                <h5 className="alert-heading fw-bold mb-1" style={{ color: "#856404" }}>⚠️ Tokenul SPV expiră în curând</h5>
                <p className="mb-0 text-muted small">
                  Tokenul curent expiră în <strong>{tokenStatus.days_left} {tokenStatus.days_left === 1 ? "zi" : "zile"}</strong> (la data {tokenStatus.expiry_date ? new Date(tokenStatus.expiry_date).toLocaleDateString("ro-RO") : "-"}).
                </p>
                <small className="text-muted d-block mt-1" style={{ fontSize: "0.78rem" }}>
                  * Notă: Reîmprospătarea se va face automat în fundal la trimiterea facturilor, fără a fi nevoie de token-ul USB, datorită Refresh Token-ului. Totuși, puteți opta să îl reînnoiți manual oricând.
                </small>
              </div>
              <Button 
                variant="warning" 
                onClick={handleOAuthRedirect} 
                className="btn-action-view px-4 py-2 fw-bold shadow-sm"
              >
                🌐 Reînnoiește Token Automat
              </Button>
            </Alert>
          )}
        </>
      )}

      {/* Header Panel */}
      <div className="gradient-header d-flex flex-wrap align-items-center justify-content-between">
        <div>
          <h3 className="mb-1 font-weight-bold" style={{ letterSpacing: "-0.5px" }}>
            Modulul eFactura ANAF SPV
            {tokenStatus && (
              <Badge 
                bg={tokenStatus.has_token ? (tokenStatus.is_expired ? "danger" : "success") : "secondary"} 
                className="ms-2 fs-6 align-middle"
              >
                {tokenStatus.has_token 
                  ? (tokenStatus.is_expired ? "Token Expirat" : `Token Activ (${tokenStatus.days_left} zile rămase)`) 
                  : "Token Lipsă"}
              </Badge>
            )}
          </h3>
          <p className="mb-0 text-muted-light" style={{ fontSize: "0.9rem", opacity: 0.8 }}>
            Gestiunea transmiterii facturilor și citirii stărilor din Spațiul Privat Virtual
          </p>
        </div>
        <div className="d-flex gap-2 mt-3 mt-sm-0">
          <Button
            variant="outline-light"
            onClick={() => setShowTokenConfig(!showTokenConfig)}
            className="btn-action-view me-2"
          >
            🔑 Configurare Token
          </Button>
          <Button
            variant="success"
            onClick={handleSendSpv}
            disabled={selectedIds.size === 0 || sending}
            className="shadow-sm px-4"
          >
            {sending ? <Spinner animation="border" size="sm" className="me-2" /> : "🚀 Trimite în SPV"} ({selectedIds.size})
          </Button>
        </div>
      </div>

      {/* Token configuration collapsible panel */}
      <Collapse in={showTokenConfig}>
        <div className="mb-4">
          <Card className="custom-card p-4">
            <h5 className="mb-3 text-dark">Configurare Chei de Securitate ANAF SPV Token</h5>

            <div className="d-flex flex-wrap gap-2 mb-4 p-3 bg-light rounded align-items-center justify-content-between border">
              <div>
                <span className="fw-semibold d-block text-dark">Obținere Automată (Recomandat)</span>
                <small className="text-muted d-block" style={{ fontSize: "0.82rem" }}>
                  Obțineți un token nou conectându-vă direct la ANAF sau reîmprospătați manual token-ul curent.
                </small>
              </div>
              <div className="d-flex gap-2">
                <Button 
                  variant="primary" 
                  onClick={handleOAuthRedirect} 
                  disabled={oauthLoading || refreshingToken}
                  className="btn-action-view shadow-sm px-3 py-2 fw-semibold"
                >
                  🌐 Obține Token Automat
                </Button>
                <Button 
                  variant="outline-primary" 
                  onClick={handleManualRefresh} 
                  disabled={refreshingToken || oauthLoading}
                  className="btn-action-view px-3 py-2 fw-semibold"
                >
                  {refreshingToken ? <Spinner animation="border" size="sm" /> : "🔄 Reîmprospătează manual"}
                </Button>
              </div>
            </div>

            <div className="border-top pt-3 mb-3">
              <span className="fw-semibold text-muted small" style={{ fontSize: "0.78rem" }}>SAU INTRODUCEȚI MANUAL TOKENUL:</span>
            </div>

            <Form onSubmit={handleSaveToken}>
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Access Token (Bearer)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      placeholder="Introduceți tokenul de acces obtinut de la ANAF OAuth..."
                      style={{ fontSize: "0.85rem" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={9}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Refresh Token</Form.Label>
                    <Form.Control
                      value={refreshTokenInput}
                      onChange={(e) => setRefreshTokenInput(e.target.value)}
                      placeholder="Introduceți tokenul de refresh..."
                      style={{ fontSize: "0.85rem" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">Expirare (Zile)</Form.Label>
                    <Form.Control
                      type="number"
                      value={tokenExpiryDays}
                      onChange={(e) => setTokenExpiryDays(Number(e.target.value))}
                    />
                  </Form.Group>
                </Col>
                <Col md={12} className="text-end">
                  <Button variant="secondary" className="me-2 btn-action-view" onClick={() => setShowTokenConfig(false)}>
                    Renunță
                  </Button>
                  <Button variant="primary" type="submit" disabled={savingToken} className="btn-action-view">
                    {savingToken ? "Se salvează..." : "Salvează Token"}
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Collapse>

      {err && (
        <Alert variant="danger" className="custom-card p-3 mb-4" onClose={() => setErr("")} dismissible>
          <pre className="mb-0" style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }}>{err}</pre>
        </Alert>
      )}

      {/* Filters card */}
      <Card className="custom-card p-3 mb-4">
        <Row className="g-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-semibold text-muted small">CĂUTARE CLIENT</Form.Label>
              <Form.Control
                value={client}
                onChange={(e) => {
                  setSkip(0);
                  setClient(e.target.value);
                }}
                placeholder="Nume client..."
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold text-muted small">NUMĂR FACTURĂ</Form.Label>
              <Form.Control
                value={docNo}
                onChange={(e) => {
                  setSkip(0);
                  setDocNo(e.target.value);
                }}
                placeholder="Număr doc..."
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-semibold text-muted small">STARE SPV</Form.Label>
              <Form.Select
                value={stareFilter}
                onChange={(e) => {
                  setSkip(0);
                  setStareFilter(e.target.value);
                }}
              >
                <option value="">Toate facturile</option>
                <option value="Nesent">De trimis (Nesent)</option>
                <option value="TRIMISA">Trimise la ANAF</option>
                <option value="ok">Validitate confirmată (ANAF OK)</option>
                <option value="EROARE">Facturi cu erori</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label className="fw-semibold text-muted small">AFIȘARE PAGINĂ</Form.Label>
              <Form.Select
                value={limit}
                onChange={(e) => {
                  setSkip(0);
                  setLimit(Number(e.target.value));
                }}
              >
                {[10, 20, 50, 100].map((x) => (
                  <option key={x} value={x}>
                    {x} randuri
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      {/* Pagination summary */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 px-1">
        <div className="text-secondary small fw-medium">
          Total: <span className="text-dark fw-bold">{total}</span> facturi • Pagina curentă: <span className="text-dark fw-bold">{Math.floor(skip / limit) + 1}</span> din <span className="text-dark fw-bold">{Math.ceil(total / limit) || 1}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {loading && <Spinner size="sm" animation="border" className="text-primary me-2" />}
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={!canPrev || loading}
            onClick={() => setSkip(Math.max(0, skip - limit))}
            className="px-3"
          >
            ◀ Înapoi
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={!canNext || loading}
            onClick={() => setSkip(skip + limit)}
            className="px-3"
          >
            Înainte ▶
          </Button>
        </div>
      </div>

      {/* Main Table */}
      <div className="custom-table table-responsive">
        <Table hover className="bg-white mb-0">
          <thead>
            <tr>
              <th className="text-center" style={{ width: 50 }}>
                <Form.Check 
                  type="checkbox" 
                  checked={allSelected} 
                  onChange={handleSelectAll} 
                  className="d-inline-block"
                />
              </th>
              <th style={{ width: 120 }}>Nr. Factură</th>
              <th>Client</th>
              <th style={{ width: 120 }}>Dată Factură</th>
              <th style={{ width: 130 }}>Dată Încărcare</th>
              <th style={{ width: 130 }} className="text-end">Total Factură</th>
              <th style={{ width: 110 }} className="text-center">Stare</th>
              <th style={{ width: 180 }}>Răspuns ANAF</th>
              <th style={{ width: 140 }}>ID Încărcare</th>
              <th style={{ width: 140 }}>ID Recepție</th>
              <th style={{ width: 160 }} className="text-center">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((it) => (
              <tr key={it.id}>
                <td className="text-center">
                  <Form.Check 
                    type="checkbox"
                    checked={selectedIds.has(it.id)}
                    onChange={(e) => handleSelectOne(it.id, e.target.checked)}
                  />
                </td>
                <td className="fw-bold text-dark">{it.doc_no || ""}</td>
                <td>{it.contact_name || `Client ID: ${it.contact_id}`}</td>
                <td>
                  {it.doc_date
                    ? new Date(it.doc_date).toLocaleDateString("ro-RO")
                    : "-"}
                </td>
                <td>
                  {it.data_incarcare
                    ? new Date(it.data_incarcare).toLocaleDateString("ro-RO")
                    : "-"}
                </td>
                <td className="text-end fw-semibold" style={{ color: "#2b2d42" }}>
                  {it.total ? it.total.toFixed(2) : "0.00"} RON
                </td>
                <td className="text-center">
                  {renderBadge(it.status_spv, it.raspuns_anaf, it.detalii)}
                </td>
                <td style={{ fontSize: "0.85rem", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={it.raspuns_anaf || ""}>
                  {it.raspuns_anaf || "-"}
                </td>
                <td className="text-monospace small" style={{ fontSize: "0.85rem" }}>{it.id_incarcare || "-"}</td>
                <td className="text-monospace small" style={{ fontSize: "0.85rem" }}>{it.id_receptie || "-"}</td>
                <td className="text-center">
                  <div className="d-flex justify-content-center gap-1">
                    <Button
                      variant="info"
                      size="sm"
                      className="btn-action-view text-white me-1"
                      disabled={checkingId === it.id || !it.id_incarcare}
                      onClick={() => handleCheckStare(it.id)}
                    >
                      {checkingId === it.id ? <Spinner animation="border" size="sm" /> : "Stare SPV"}
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="btn-action-view"
                      disabled={downloadingId === it.id}
                      onClick={() => handleDownloadPdf(it.id, it.doc_no)}
                    >
                      {downloadingId === it.id ? <Spinner animation="border" size="sm" /> : "PDF"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center text-muted py-5">
                  Nicio factură de exportat/interogat în SPV.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Container>
  );
}
